import React, { lazy } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import qs from 'qs';

import PageNotFound from '@common/components/page-not-found';
import { V2_ROUTE_PAGES } from '@aware/v2-routes/constants';
import { useGetRoutes } from '@aware/hooks';
import AwareUtilityBar from '@aware/common/utility-bar';

const V2HelpPage = lazy(
  () => import(/* webpackChunkName: "aware-help-page" */ '@aware/pages/v2-help/v2-help')
);

const AppRoutes = () => {
  const { search } = useLocation();
  const { allRoutes } = useGetRoutes();

  return (
    <Switch>
      {/*******************
       * Redirects should be added before defining routes
       *
       */}

      <Route path={V2_ROUTE_PAGES.help}>
        <V2HelpPage />
      </Route>

      <Route
        path="/"
        exact
        render={({ location }) => {
          const { alert, formId, studentId } = qs.parse(location.search, {
            ignoreQueryPrefix: true
          });

          if (alert === '1000') {
            return (
              <Redirect
                to={`${V2_ROUTE_PAGES.itemBankSettings}/approve-pending-questions`}
              />
            );
          }

          if (alert === '1100') {
            return (
              <Redirect
                to={`${V2_ROUTE_PAGES.studentProfiles}/${studentId}?formId=${formId}`}
              />
            );
          }

          if (alert === '1200') {
            return (
              <Redirect
                to={`${V2_ROUTE_PAGES.studentSettings}/approve-student-access-lists`}
              />
            );
          }
          return <Redirect to="overview" />;
        }}
      />

      {/* Insert some common Classic routes for Cypress testing and backwards compat */}
      <Route
        path="/answers/:params*"
        render={({
          match: {
            params: { params }
          }
        }) => (
          <Redirect to={{ pathname: `${V2_ROUTE_PAGES.enterAnswers}/${params}`, search }} />
        )}
      />

      <Route
        path="/tests-available"
        render={() => (
          <Redirect
            to={{
              pathname: `${V2_ROUTE_PAGES.administerAssessments}/main`,
              search
            }}
          />
        )}
      />

      <Route
        path="/test-creation"
        render={() => (
          <Redirect
            to={{
              pathname: `${V2_ROUTE_PAGES.authorAssessments}/create`,
              search
            }}
          />
        )}
      />

      <Route
        path="/test-editor/:params*"
        render={({
          match: {
            params: { params }
          }
        }) => (
          <Redirect
            to={{ pathname: `${V2_ROUTE_PAGES.authorAssessments}/edit/${params}`, search }}
          />
        )}
      />

      <Route
        path="/itembank/:params*"
        render={({
          match: {
            params: { params = '' }
          }
        }) => (
          <Redirect
            to={{
              pathname: `${V2_ROUTE_PAGES.itemBankQuestionsAndResources}/${params}`,
              search
            }}
          />
        )}
      />

      {/* Normal V2 Routes */}
      {allRoutes.map(({ routes: subRoutes = [], ...routeProps }, idx) => {
        // Destructure data we'll need to set explicitly
        const { path: routePath, component: routeComponent } = routeProps;

        return (
          <Route
            key={idx}
            path={routePath}
            render={() => {
              return (
                <>
                  <Route {...routeProps} />
                  {Boolean(subRoutes.length) &&
                    subRoutes.map((subRoute, subIdx) => {
                      // Destructure data we'll need to set explicitly
                      const {
                        path: subRoutePath,
                        component: subRouteComponent,
                        ...subRouteProps
                      } = subRoute;

                      return (
                        <Route
                          key={subIdx}
                          {...subRouteProps}
                          path={`${subRoute.path}`}
                          component={subRoute.component || routeComponent}
                        />
                      );
                    })}
                </>
              );
            }}
          />
        );
      })}
      {/**
       * If route is not matched above, show 404 page
       *
       */}
      <Route>
        <PageNotFound UtilityBar={AwareUtilityBar} />
      </Route>
    </Switch>
  );
};

export default AppRoutes;
